import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, api } from '../App';

const ALL_PLATFORMS = {
  facebook:        { label: 'Facebook',  color: '#1877f2', short: 'FB' },
  instagram:       { label: 'Instagram', color: '#e1306c', short: 'IG', photoOnly: true },
  linkedin:        { label: 'LinkedIn',  color: '#0077b5', short: 'LI' },
  google_business: { label: 'Google',    color: '#ea4335', short: 'G'  },
  gbp:             { label: 'Google',    color: '#ea4335', short: 'G'  },
  x:               { label: 'X',         color: '#aaaaaa', short: 'X'  },
  tiktok:          { label: 'TikTok',    color: '#69c9d0', short: 'TT', photoOnly: true },
  threads:         { label: 'Threads',   color: '#888888', short: 'TH' },
  pinterest:       { label: 'Pinterest', color: '#e60023', short: 'PI', photoOnly: true },
  reddit:          { label: 'Reddit',    color: '#ff4500', short: 'RE' },
  bluesky:         { label: 'Bluesky',   color: '#0085ff', short: 'BS' },
  youtube:         { label: 'YouTube',   color: '#ff0000', short: 'YT', photoOnly: true },
};

const TEXT_PLATFORMS   = ['facebook','linkedin','x','threads','reddit','bluesky','google_business'];
const PHOTO_ONLY_PLATS = ['instagram','tiktok','youtube','pinterest'];

const BEST_TIMES = {
  instagram:       { hint: 'Mi–Do · 11–13 oder 18–20 Uhr',  time: '12:00' },
  linkedin:        { hint: 'Di–Do · 11–14 oder 17–18 Uhr',  time: '11:00' },
  facebook:        { hint: 'Mo, Mi · 12–14 Uhr',             time: '12:00' },
  x:               { hint: 'Di–Do · 12–15 Uhr',              time: '12:00' },
  threads:         { hint: 'Mi–Do · 9–12 Uhr',               time: '09:00' },
  pinterest:       { hint: 'Fr–Sa · 20–23 Uhr',              time: '20:00' },
  reddit:          { hint: 'Mo–Fr · 6–8 oder 12–13 Uhr',    time: '12:00' },
  bluesky:         { hint: 'Mi · 13–15 Uhr',                 time: '13:00' },
  google_business: { hint: 'Di–Do · 9–11 Uhr',               time: '09:00' },
};
const IMAGE_STYLES     = [
  { id: 'professional', label: 'Professional', icon: '🏢' },
  { id: 'bold',         label: 'Bold',         icon: '⚡' },
  { id: 'minimal',      label: 'Minimal',      icon: '◻' },
  { id: 'warm',         label: 'Warm',         icon: '☀' },
];

// ── Haupt-Komponente ──────────────────────────────────────────────────────────
export default function History() {
  const { token, user }  = useAuth();
  const location   = useLocation();
  const [posts,    setPosts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(location.state?.expandPostId || null);
  const [filter,   setFilter]   = useState('all');
  const [view,     setView]     = useState('posts');
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [blogDrafts,   setBlogDrafts]   = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(false);

  function loadBlogs() {
    setBlogsLoading(true);
    api('/blog', {}, token).then(setBlogDrafts).catch(console.error).finally(() => setBlogsLoading(false));
  }

  useEffect(() => {
    api('/uploadpost/status', {}, token).then(d => { if (d.connected) setConnectedPlatforms(d.platforms || []); }).catch(() => {});
  }, [token]);

  useEffect(() => {
    api('/posts', {}, token).then(data => {
      setPosts(data);
      if (location.state?.expandPostId) {
        setTimeout(() => document.getElementById(`post-${location.state.expandPostId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
      }
    }).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  function reloadPosts() { api('/posts', {}, token).then(setPosts).catch(console.error); }

  function jumpToPost(postId) {
    setView('posts');
    setFilter('all');
    setExpanded(postId);
    setTimeout(() => document.getElementById(`post-${postId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
  }

  const filtered = posts.filter(p => {
    if (filter === 'scheduled') return p.variants?.some(v => v.scheduled_for);
    if (filter === 'approved')  return p.variants?.some(v => v.approved);
    if (filter === 'draft')     return !p.variants?.some(v => v.approved) && !p.variants?.some(v => v.scheduled_for);
    return true;
  });

  async function handleApprove(postId, variantId, current) {
    try {
      const updated = await api(`/posts/variants/${variantId}`, { method: 'PATCH', body: JSON.stringify({ approved: !current }) }, token);
      setPosts(prev => prev.map(p => p.id !== postId ? p : { ...p, variants: p.variants.map(v => v.id === updated.id ? { ...v, ...updated } : v) }));
    } catch {}
  }

  async function handleSaveEdit(postId, variantId, content) {
    try {
      const updated = await api(`/posts/variants/${variantId}`, { method: 'PATCH', body: JSON.stringify({ content }) }, token);
      setPosts(prev => prev.map(p => p.id !== postId ? p : { ...p, variants: p.variants.map(v => v.id === updated.id ? { ...v, ...updated } : v) }));
    } catch {}
  }

  async function handleDelete(postId) {
    const post        = posts.find(p => p.id === postId);
    const wasPublished = post?.variants?.some(v => v.published);
    const msg = wasPublished
      ? 'Dieser Post wurde bereits veröffentlicht.\n\nAus \'Meine Posts\' löschen? Der veröffentlichte Inhalt auf den Plattformen bleibt bestehen.'
      : 'Post wirklich löschen?';
    if (!window.confirm(msg)) return;
    try {
      await api(`/posts/${postId}`, { method: 'DELETE' }, token);
      setPosts(p => p.filter(x => x.id !== postId));
      if (expanded === postId) setExpanded(null);
    } catch {}
  }

  return (
    <div style={{ padding: '24px', maxWidth: 900 }}>
      <div className="fade-up" style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Post <span className="gradient-text">Verlauf</span></h1>
        <p style={{ color: 'var(--text2)', fontSize: 13 }}>
          Alle generierten Posts — bearbeiten, freigeben, Bild generieren, veröffentlichen.
          {connectedPlatforms.length > 0 && <span style={{ color: 'var(--green)', marginLeft: 8, fontSize: 11 }}>● {connectedPlatforms.length} Kanal{connectedPlatforms.length > 1 ? 'e' : ''} verbunden</span>}
        </p>
      </div>

      <div className="fade-up" style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[{ id: 'posts', label: 'Meine Posts', icon: '◎' }, { id: 'published', label: 'Veröffentlicht', icon: '⊹' }, { id: 'blog', label: 'Blog-Entwürfe', icon: '📝' }].map(t => (
          <button key={t.id} onClick={() => { setView(t.id); if (t.id === 'blog') loadBlogs(); }} style={{ padding: '8px 18px', borderRadius: 10, fontSize: 13, fontWeight: view===t.id ? 600 : 400, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, background: view===t.id ? 'linear-gradient(135deg, var(--cyan-dim), var(--blue-dim))' : 'var(--glass)', color: view===t.id ? 'var(--cyan)' : 'var(--text2)', border: `1px solid ${view===t.id ? 'rgba(0,255,255,0.25)' : 'var(--glass-border)'}`, transition: 'all 0.15s' }}>{t.icon} {t.label}</button>
        ))}
      </div>

      {view === 'posts' && (
        <>
          <div className="fade-up" style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
            {[
              { id: 'all',       label: `Alle (${posts.length})`,                                                                                               color: null },
              { id: 'scheduled', label: `Geplant (${posts.filter(p => p.variants?.some(v => v.scheduled_for)).length})`,                                        color: '#7c3aed' },
              { id: 'approved',  label: `Freigegeben (${posts.filter(p => p.variants?.some(v => v.approved)).length})`,                                         color: null },
              { id: 'draft',     label: `Entwürfe (${posts.filter(p => !p.variants?.some(v => v.approved) && !p.variants?.some(v => v.scheduled_for)).length})`, color: null },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)} style={{ padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', background: filter===f.id ? (f.color ? 'rgba(124,58,237,0.12)' : 'var(--cyan-dim)') : 'var(--glass)', color: filter===f.id ? (f.color || 'var(--cyan)') : 'var(--text2)', border: `1px solid ${filter===f.id ? (f.color ? 'rgba(124,58,237,0.35)' : 'rgba(0,255,255,0.25)') : 'var(--glass-border)'}`, transition: 'all 0.15s' }}>{f.label}</button>
            ))}
          </div>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <div key={i} className="shimmer" style={{ height: 68, borderRadius: 12 }} />)}</div>
          ) : filtered.length === 0 ? (
            <div className="glass" style={{ padding: 40, textAlign: 'center', borderStyle: 'dashed' }}><div style={{ fontSize: 24, opacity: 0.3, marginBottom: 12 }}>◎</div><p style={{ color: 'var(--text2)', fontSize: 13 }}>Keine Posts gefunden.</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map((post, i) => (
                <PostItem key={post.id} post={post} expanded={expanded === post.id}
                  onToggle={() => setExpanded(expanded === post.id ? null : post.id)}
                  onApprove={handleApprove} onSaveEdit={handleSaveEdit} onDelete={handleDelete}
                  delay={i * 0.03} token={token} connectedPlatforms={connectedPlatforms}
                  onReload={reloadPosts} userTier={user?.tier || 'basic'} />
              ))}
            </div>
          )}
        </>
      )}
      {view === 'published' && <PublishedTab token={token} onJumpToPost={jumpToPost} />}
      {view === 'blog'      && <BlogTab token={token} drafts={blogDrafts} loading={blogsLoading} onReload={loadBlogs} />}
    </div>
  );
}

// ── Veröffentlicht Tab ────────────────────────────────────────────────────────
function PublishedTab({ token, onJumpToPost }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  useEffect(() => {
    api('/uploadpost/history', {}, token).then(d => setHistory(d.posts || [])).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [token]);
  if (loading) return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <div key={i} className="shimmer" style={{ height: 68, borderRadius: 12 }} />)}</div>;
  if (error) return <div className="glass" style={{ padding: 20 }}><p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p></div>;
  if (history.length === 0) return (
    <div className="glass" style={{ padding: 40, textAlign: 'center', borderStyle: 'dashed' }}>
      <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.3 }}>⊹</div>
      <p style={{ color: 'var(--text2)', fontSize: 13 }}>Noch keine veröffentlichten Posts.</p>
    </div>
  );
  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{history.length} Posts</p>
      {history.map((post, i) => <PublishedPostItem key={post.variant_id || post.request_id || i} post={post} onJumpToPost={onJumpToPost} />)}
    </div>
  );
}

// ── PublishedPostItem: Topic als Titel, Content als Text, Link wenn vorhanden ─
function PublishedPostItem({ post, onJumpToPost }) {
  const title    = post.post_title || '';
  const content  = post.post_content || post.post_caption || '';
  const platform = (post.platform || '').toLowerCase();
  const pl       = ALL_PLATFORMS[platform] || { label: platform || 'Unbekannt', color: '#888', short: '?' };
  const date     = post.upload_timestamp;
  const isFailed = post.success === false;
  const [expanded, setExpanded] = useState(false);
  const isLong = content.length > 160;

  return (
    <div className="fade-up glass" style={{ overflow: 'hidden', border: isFailed ? '1px solid rgba(255,0,85,0.3)' : undefined }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', cursor: isLong ? 'pointer' : 'default' }} onClick={() => isLong && setExpanded(e => !e)}>
        <div style={{ width: 44, height: 44, borderRadius: 8, flexShrink: 0, background: `${pl.color}18`, border: `1px solid ${pl.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: pl.color }}>{pl.short}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {title && (
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 5, lineHeight: 1.4 }}>{title}</p>
          )}
          <p style={{ fontSize: 12, color: content ? 'var(--text2)' : 'var(--muted)', marginBottom: 4, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {content
              ? (expanded ? content : content.length > 160 ? content.slice(0, 160) + '…' : content)
              : <em style={{ opacity: 0.5 }}>Kein Text</em>}
          </p>
          {isLong && <span style={{ fontSize: 10, color: 'var(--cyan)' }}>{expanded ? '▲ weniger' : '▼ mehr anzeigen'}</span>}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20, background: `${pl.color}18`, color: pl.color }}>{pl.label}</span>
            {date && <span style={{ fontSize: 10, color: 'var(--muted)' }}>{new Date(date).toLocaleString('de-DE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
            {post.post_url && (
              <a href={post.post_url} target="_blank" rel="noreferrer"
                style={{ fontSize: 10, color: 'var(--cyan)', display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 8px', borderRadius: 10, background: 'var(--cyan-dim)', border: '1px solid rgba(0,255,255,0.2)' }}
                onClick={e => e.stopPropagation()}>↗ Zum Post</a>
            )}
          </div>
          {isFailed && post.error_message && <p style={{ fontSize: 11, color: 'var(--red)', marginTop: 5, padding: '5px 8px', background: 'var(--red-dim)', borderRadius: 6 }}>{post.error_message}</p>}
        </div>
        <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 20, flexShrink: 0, marginTop: 2, background: isFailed ? 'var(--red-dim)' : 'rgba(0,255,136,0.08)', color: isFailed ? 'var(--red)' : 'var(--green)', border: `1px solid ${isFailed ? 'rgba(255,0,85,0.25)' : 'rgba(0,255,136,0.2)'}` }}>
          {isFailed ? '✕ Fehler' : '✓ Gepostet'}
        </span>
      </div>
    </div>
  );
}

// ── Post Item ─────────────────────────────────────────────────────────────────
function PostItem({ post, expanded, onToggle, onApprove, onSaveEdit, onDelete, delay, token, connectedPlatforms, onReload, userTier = 'basic' }) {
  const [showPostPublish, setShowPostPublish] = useState(false);
  const [showImgPanel,    setShowImgPanel]    = useState(false);
  const [postImageData,   setPostImageData]   = useState(null);
  const [postSavedImage,  setPostSavedImage]  = useState(null);

  React.useEffect(() => {
    api('/images/post/' + post.id, {}, token)
      .then(d => { if (d && d.image_data) setPostSavedImage(d); })
      .catch(() => {});
  }, [post.id]);

  const activePostImage = postImageData || (postSavedImage && postSavedImage.image_data) || null;
  const allApproved   = post.variants?.every(v => v.approved);
  const someApproved  = post.variants?.some(v => v.approved);
  const hasScheduled  = post.variants?.some(v => v.scheduled_for);
  const date = new Date(post.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });

  const publishablePlatforms = (post.variants || [])
    .filter(v => v.approved && connectedPlatforms.includes(v.platform))
    .map(v => v.platform);
  const canPublishAll = publishablePlatforms.length > 0;

  return (
    <div id={`post-${post.id}`} className="fade-up glass" style={{ overflow: 'hidden', border: `1px solid ${hasScheduled ? 'rgba(109,40,217,0.25)' : showPostPublish ? 'rgba(0,255,136,0.3)' : expanded ? 'rgba(0,255,255,0.2)' : 'var(--glass-border)'}`, animationDelay: `${delay}s` }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px' }}>
        <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: 'pointer' }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: hasScheduled ? 'rgba(109,40,217,0.1)' : allApproved ? 'rgba(0,255,136,0.1)' : someApproved ? 'var(--cyan-dim)' : 'var(--glass2)', border: `1px solid ${hasScheduled ? 'rgba(109,40,217,0.3)' : allApproved ? 'rgba(0,255,136,0.25)' : someApproved ? 'rgba(0,255,255,0.2)' : 'var(--glass-border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: hasScheduled ? '#7c3aed' : allApproved ? 'var(--green)' : someApproved ? 'var(--cyan)' : 'var(--muted)' }}>
            {hasScheduled ? '📅' : allApproved ? '✓' : someApproved ? '◐' : '○'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
              <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{post.topic}</div>
              {hasScheduled && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 20, background: 'rgba(124,58,237,0.12)', color: '#7c3aed', border: '1px solid rgba(124,58,237,0.3)', flexShrink: 0, letterSpacing: '0.04em' }}>📅 GEPLANT</span>}
            </div>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
              {(post.variants || []).map(v => {
                const pl = ALL_PLATFORMS[v.platform] || { color: '#888', short: '?' };
                return <span key={v.id} style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: v.scheduled_for ? 'rgba(109,40,217,0.1)' : `${pl.color}18`, color: v.scheduled_for ? '#7c3aed' : v.approved ? pl.color : 'var(--muted)', display: 'flex', alignItems: 'center', gap: 2 }}>{v.scheduled_for && '📅'}{pl.short}</span>;
              })}
              <span style={{ fontSize: 10, color: 'var(--muted)' }}>{date}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
          <button onClick={() => setShowImgPanel(p => !p)} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 500, background: showImgPanel ? 'var(--cyan-dim)' : activePostImage ? 'rgba(0,212,255,0.08)' : 'var(--glass)', color: showImgPanel ? 'var(--cyan)' : activePostImage ? 'var(--cyan)' : 'var(--text2)', border: '1px solid ' + (showImgPanel ? 'rgba(0,255,255,0.3)' : activePostImage ? 'rgba(0,212,255,0.2)' : 'var(--glass-border)'), whiteSpace: 'nowrap' }}>
            {activePostImage ? '✓ Bild' : '🖼 Bild'}
          </button>
          {connectedPlatforms.length > 0 && canPublishAll && (
            <button onClick={() => { setShowPostPublish(p => !p); }} style={{ fontSize: 12, padding: '6px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, background: showPostPublish ? 'rgba(0,255,136,0.2)' : 'rgba(0,255,136,0.08)', color: 'var(--green)', border: `1px solid ${showPostPublish ? 'rgba(0,255,136,0.5)' : 'rgba(0,255,136,0.3)'}`, transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
              ⊹ Veröffentlichen
            </button>
          )}
          <button className="btn-ghost" onClick={() => onDelete(post.id)} style={{ fontSize: 11, padding: '4px 10px', color: 'var(--text2)' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text2)'}>Löschen</button>
          <div onClick={onToggle} style={{ color: 'var(--muted)', fontSize: 11, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none', cursor: 'pointer', padding: '4px' }}>▾</div>
        </div>
      </div>

      {showPostPublish && (
        <PostPublishPanel post={post} token={token} connectedPlatforms={connectedPlatforms} publishablePlatforms={publishablePlatforms} onClose={() => setShowPostPublish(false)} onReload={onReload} userTier={userTier} sharedImage={activePostImage} />
      )}

      {showImgPanel && (
        <PostImagePanel postId={post.id} topic={post.topic} firstPlatform={post.variants && post.variants[0] ? post.variants[0].platform : 'instagram'} token={token} userTier={userTier} imageData={postImageData} savedImage={postSavedImage} onImageChange={(data) => { setPostImageData(data); if (!data) setPostSavedImage(null); }} onClose={() => setShowImgPanel(false)} />
      )}

      {expanded && (
        <div className="fade-in" style={{ borderTop: '1px solid var(--glass-border)', padding: '12px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(post.variants || []).map(v => (
              <VariantRow key={v.id} variant={v} topic={post.topic} token={token}
                onApprove={() => onApprove(post.id, v.id, v.approved)}
                onSave={content => onSaveEdit(post.id, v.id, content)}
                connectedPlatforms={connectedPlatforms} onReload={onReload} userTier={userTier} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PostImagePanel({ postId, topic, firstPlatform, token, userTier, imageData, savedImage, onImageChange, onClose }) {
  const [imgLoading,   setImgLoading]   = React.useState(false);
  const [imgError,     setImgError]     = React.useState('');
  const [imgStyle,     setImgStyle]     = React.useState('professional');
  const [provider,     setProvider]     = React.useState('gemini');
  const [applyLogo,    setApplyLogo]    = React.useState(true);
  const [companyLogo,  setCompanyLogo]  = React.useState(null);
  const [customPrompt, setCustomPrompt] = React.useState('');
  const [showCustom,   setShowCustom]   = React.useState(false);
  const [uploading,    setUploading]    = React.useState(false);
  const fileRef = React.useRef(null);

  React.useEffect(() => {
    api('/company', {}, token).then(d => setCompanyLogo(d && d.logo_data ? d.logo_data : false)).catch(() => setCompanyLogo(false));
  }, []);

  const activeImage = imageData || (savedImage && savedImage.image_data) || null;

  async function generate(customP) {
    setImgLoading(true); setImgError('');
    try {
      const body = { topic, platform: firstPlatform || 'instagram', style: imgStyle, post_id: postId, provider, apply_logo: applyLogo && !!companyLogo };
      if (customP) body.custom_prompt = customP;
      const d = await api('/images/generate', { method: 'POST', body: JSON.stringify(body) }, token);
      onImageChange(d.image);
    } catch (e) { setImgError(e.message); }
    finally { setImgLoading(false); }
  }

  async function handleUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true); setImgError('');
    try {
      const fd = new FormData();
      fd.append('image', file); fd.append('post_id', postId); fd.append('platform', firstPlatform || 'instagram');
      const res = await fetch('/api/images/upload', { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload fehlgeschlagen');
      onImageChange(data.image);
    } catch (e) { setImgError(e.message); }
    finally { setUploading(false); }
  }

  return (
    <div className="fade-in" style={{ borderTop: '2px solid rgba(0,212,255,0.2)', padding: '14px 16px', background: 'var(--glass2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--cyan)', letterSpacing: '0.08em' }}>BILD FUER DIESEN POST</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>x</button>
      </div>
      {activeImage && (
        <div style={{ marginBottom: 12 }}>
          <img src={activeImage} alt="Bild" style={{ width: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--glass-border2)', background: 'var(--glass)' }} />
          <div style={{ display: 'flex', gap: 7, marginTop: 6 }}>
            <button onClick={() => { const a = document.createElement('a'); a.href = activeImage; a.download = 'bild.png'; a.click(); }} className="btn-ghost" style={{ fontSize: 10 }}>Herunterladen</button>
            <button onClick={() => onImageChange(null)} className="btn-ghost" style={{ fontSize: 10, color: 'var(--red)' }}>Entfernen</button>
          </div>
        </div>
      )}
      {['team','agency'].includes(userTier) && (
        <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
          {[{ id: 'gemini', label: 'Gemini' }, { id: 'flux', label: 'Flux Pro' }].map(prov => (
            <button key={prov.id} onClick={() => setProvider(prov.id)} style={{ padding: '4px 12px', borderRadius: 7, fontSize: 11, cursor: 'pointer', background: provider===prov.id ? (prov.id==='flux' ? 'rgba(200,164,90,0.12)' : 'var(--cyan-dim)') : 'transparent', color: provider===prov.id ? (prov.id==='flux' ? 'var(--gold)' : 'var(--cyan)') : 'var(--text2)', border: '1px solid ' + (provider===prov.id ? (prov.id==='flux' ? 'rgba(200,164,90,0.4)' : 'rgba(0,255,255,0.3)') : 'var(--glass-border)') }}>{prov.label}</button>
          ))}
        </div>
      )}
      {companyLogo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '6px 10px', background: 'var(--glass)', borderRadius: 7, border: '1px solid var(--glass-border)' }}>
          <img src={companyLogo} alt="Logo" style={{ height: 20, maxWidth: 50, objectFit: 'contain' }} />
          <span style={{ fontSize: 11, color: 'var(--text2)', flex: 1 }}>Logo einblenden</span>
          <button onClick={() => setApplyLogo(p => !p)} style={{ width: 32, height: 18, borderRadius: 9, cursor: 'pointer', border: 'none', background: applyLogo ? 'var(--green)' : 'var(--glass-border2)', position: 'relative' }}>
            <span style={{ position: 'absolute', top: 1, left: applyLogo ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div style={{ padding: 10, background: 'var(--glass)', borderRadius: 8, border: '1px solid var(--glass-border)' }}>
          <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 7 }}>KI generieren</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
            {IMAGE_STYLES.map(s => <button key={s.id} onClick={() => setImgStyle(s.id)} style={{ padding: '2px 7px', borderRadius: 5, fontSize: 10, cursor: 'pointer', background: imgStyle===s.id ? 'var(--cyan-dim)' : 'transparent', color: imgStyle===s.id ? 'var(--cyan)' : 'var(--text2)', border: '1px solid ' + (imgStyle===s.id ? 'rgba(0,255,255,0.25)' : 'var(--glass-border)') }}>{s.icon}</button>)}
          </div>
          <button onClick={() => generate(null)} disabled={imgLoading} className="btn-primary" style={{ fontSize: 10, padding: '5px 12px', width: '100%' }}>
            {imgLoading ? 'Generiert...' : activeImage ? 'Neu generieren' : 'Generieren'}
          </button>
        </div>
        <div style={{ padding: 10, background: 'var(--glass)', borderRadius: 8, border: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 7 }}>Hochladen</div>
          <p style={{ fontSize: 9, color: 'var(--muted)', flex: 1 }}>JPG, PNG, WebP max. 8 MB</p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} style={{ display: 'none' }} />
          <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading} className="btn-ghost" style={{ fontSize: 10, padding: '5px 12px' }}>{uploading ? '...' : 'Upload'}</button>
        </div>
      </div>
      <div style={{ padding: '7px 10px', background: 'var(--glass)', borderRadius: 8, border: '1px solid var(--glass-border)' }}>
        <button onClick={() => setShowCustom(p => !p)} style={{ fontSize: 10, fontWeight: 600, color: showCustom ? 'var(--cyan)' : 'var(--text2)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Eigener Prompt</button>
        {showCustom && (
          <div style={{ marginTop: 7 }}>
            <textarea value={customPrompt} onChange={e => setCustomPrompt(e.target.value)} placeholder="Bildbeschreibung..." rows={2} className="input" style={{ fontSize: 11, resize: 'vertical', marginBottom: 6 }} />
            <button onClick={() => generate(customPrompt)} disabled={imgLoading || !customPrompt.trim()} className="btn-primary" style={{ fontSize: 10, padding: '5px 12px' }}>Generieren</button>
          </div>
        )}
      </div>
      {imgError && <p style={{ fontSize: 11, color: 'var(--red)', marginTop: 6 }}>{imgError}</p>}
    </div>
  );
}

function PostPublishPanel({ post, token, connectedPlatforms, publishablePlatforms, onClose, onReload, userTier = 'basic', sharedImage = null }) {
  const photoOnlyPlatforms = PHOTO_ONLY_PLATS.filter(p => publishablePlatforms.includes(p));
  const textPlatforms      = publishablePlatforms.filter(p => TEXT_PLATFORMS.includes(p));
  const [selectedPlats,    setSelectedPlats]    = useState(publishablePlatforms);
  const [mode,             setMode]             = useState('now');
  const [scheduleMap,      setScheduleMap]      = useState({});
  const [quickDate,        setQuickDate]        = useState('');
  const [quickTime,        setQuickTime]        = useState('09:00');
  const [imageData,        setImageData]        = useState(sharedImage || null);
  const [savedImage,       setSavedImage]       = useState(null);
  const [imageLoading,     setImageLoading]     = useState(false);
  const [imageStyle,       setImageStyle]       = useState('professional');
  const [imageProvider,    setImageProvider]    = useState('gemini');
  const [uploading,        setUploading]        = useState(false);
  const [noImagePlatforms, setNoImagePlatforms] = useState([]);
  const [publishing,       setPublishing]       = useState(false);
  const [result,           setResult]           = useState(null);
  const [error,            setError]            = useState('');
  const [imageError,       setImageError]       = useState('');
  const [showImgSection,   setShowImgSection]   = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (sharedImage) { setImageData(sharedImage); setShowImgSection(true); return; }
    if (post.id) {
      api('/images/post/' + post.id, {}, token).then(d => { if (d && d.image_data) { setSavedImage(d); setShowImgSection(true); } }).catch(() => {});
    }
  }, []);

  const activeImage = imageData || savedImage?.image_data || null;
  const minDate     = new Date(Date.now() + 10 * 60000).toISOString().slice(0, 10);
  const hasSchedule = mode === 'later' && selectedPlats.length > 0 && selectedPlats.every(pl => scheduleMap[pl]?.date);
  const gbpVariant  = post.variants?.find(v => v.platform === 'google_business');
  const gbpText     = gbpVariant ? gbpVariant.content.replace(/\s*#\w+/g, '').trim() : '';
  const gbpTooLong  = selectedPlats.includes('google_business') && gbpText.length > 1500;
  const canPublish  = selectedPlats.length > 0 && !gbpTooLong && (mode === 'now' || hasSchedule);

  React.useEffect(() => {
    setScheduleMap(prev => {
      const next = {};
      for (const pl of selectedPlats) next[pl] = prev[pl] || { date: '', time: '09:00' };
      return next;
    });
  }, [selectedPlats.join(',')]);

  function togglePlatform(id) { setSelectedPlats(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); }
  function toggleNoImage(id)  { setNoImagePlatforms(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); }

  async function handleGenerateImage() {
    setImageLoading(true); setImageError('');
    try {
      const firstVariant = post.variants?.[0];
      const data = await api('/images/generate', { method: 'POST', body: JSON.stringify({ topic: post.topic, platform: firstVariant?.platform || 'instagram', style: imageStyle, provider: imageProvider, post_id: post.id }) }, token);
      setImageData(data.image); setSavedImage(null); setShowImgSection(true);
    } catch (err) { setImageError(err.message); }
    finally { setImageLoading(false); }
  }

  async function handleUploadImage(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setImageError('');
    try {
      const firstVariant = post.variants?.[0];
      const fd = new FormData();
      fd.append('image', file); fd.append('variant_id', firstVariant?.id || ''); fd.append('platform', firstVariant?.platform || 'instagram');
      const res  = await fetch('/api/images/upload', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload fehlgeschlagen');
      setImageData(data.image); setSavedImage(null); setShowImgSection(true);
    } catch (err) { setImageError(err.message); }
    finally { setUploading(false); }
  }

  async function handlePublish() {
    setPublishing(true); setError('');
    const scheduleMapForApi = mode === 'later'
      ? Object.fromEntries(selectedPlats.filter(pl => scheduleMap[pl]?.date).map(pl => [pl, `${scheduleMap[pl].date}T${scheduleMap[pl].time || '09:00'}:00`]))
      : null;
    const hasAnySchedule = scheduleMapForApi && Object.keys(scheduleMapForApi).length > 0;
    try {
      const res = await api('/uploadpost/publish-post', { method: 'POST', body: JSON.stringify({ post_id: post.id, platforms: selectedPlats, schedule_map: hasAnySchedule ? scheduleMapForApi : undefined, image_data: activeImage || undefined, no_image_platforms: noImagePlatforms }) }, token);
      setResult({ scheduled: hasAnySchedule, scheduleMap: scheduleMapForApi, results: res.results || [], warnings: res.warnings || [] });
      if (onReload) setTimeout(onReload, 500);
    } catch (err) { setError(err.message); }
    finally { setPublishing(false); }
  }

  if (result) {
    const succeeded = result.results.filter(r => r.success);
    const failed    = result.results.filter(r => !r.success && !r.skipped);
    const blocked   = result.results.filter(r => r.skipped);
    const allBlocked = blocked.length > 0 && succeeded.length === 0 && failed.length === 0;
    const headerText = succeeded.length > 0
      ? result.scheduled
        ? `📅 Geplant auf ${succeeded.length} Plattform${succeeded.length !== 1 ? 'en' : ''}`
        : `✓ Veröffentlicht auf ${succeeded.length} Plattform${succeeded.length !== 1 ? 'en' : ''}`
      : '⚠ Inhalt konnte nicht gepostet werden';
    const headerColor = succeeded.length > 0 ? 'var(--green)' : 'var(--gold)';
    return (
      <div className="fade-in" style={{ borderTop: '1px solid var(--glass-border)', padding: '16px 18px', background: allBlocked ? 'rgba(255,170,0,0.04)' : succeeded.length > 0 ? 'var(--green-dim)' : 'rgba(255,0,85,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span className="dot-live" />
              <span style={{ fontSize: 13, color: headerColor, fontWeight: 600 }}>{headerText}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {result.results.map(r => {
                const pl = ALL_PLATFORMS[r.platform] || { label: r.platform, color: '#888' };
                const schedTime = result.scheduleMap?.[r.platform] ? new Date(result.scheduleMap[r.platform]).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
                return (
                  <div key={r.platform} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: r.success ? `${pl.color}18` : r.skipped ? 'var(--glass2)' : 'var(--red-dim)', color: r.success ? pl.color : r.skipped ? 'var(--muted)' : 'var(--red)', border: `1px solid ${r.success ? pl.color + '40' : r.skipped ? 'var(--glass-border)' : 'rgba(255,0,85,0.3)'}` }}>{r.success ? '✓ ' : r.skipped ? '— ' : '✕ '}{pl.label}</span>
                    {r.success && schedTime && <span style={{ fontSize: 10, color: 'var(--muted)', paddingLeft: 10, marginTop: 2 }}>📅 {schedTime}</span>}
                  </div>
                );
              })}
            </div>
            {result.warnings?.length > 0 && <div style={{ padding: '8px 12px', background: 'rgba(255,170,0,0.06)', border: '1px solid rgba(255,170,0,0.2)', borderRadius: 8, marginBottom: 8 }}>{result.warnings.map((w, i) => <p key={i} style={{ fontSize: 11, color: 'var(--gold)', lineHeight: 1.6 }}>⚠ {w}</p>)}</div>}
            {failed.length > 0 && <div style={{ padding: '6px 10px', background: 'var(--red-dim)', border: '1px solid rgba(255,0,85,0.2)', borderRadius: 7, marginBottom: 8 }}>{failed.map(r => <p key={r.platform} style={{ fontSize: 11, color: 'var(--red)' }}>{ALL_PLATFORMS[r.platform]?.label || r.platform}: {r.error}</p>)}</div>}
            {blocked.length > 0 && (
              <div style={{ padding: '10px 12px', background: 'rgba(255,170,0,0.05)', border: '1px solid rgba(255,170,0,0.2)', borderRadius: 8 }}>
                <p style={{ fontSize: 12, color: 'var(--gold)', marginBottom: 8, lineHeight: 1.5 }}>
                  {allBlocked ? 'Der KI-Check hat den Inhalt blockiert. Bitte überarbeite den Text und versuche es erneut.' : 'Einige Plattformen wurden vom KI-Check blockiert.'}
                </p>
                <button onClick={onClose} className="btn-ghost" style={{ fontSize: 11, padding: '5px 12px' }}>✎ Text bearbeiten und erneut versuchen →</button>
              </div>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost" style={{ fontSize: 11, flexShrink: 0 }}>Schließen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ borderTop: '2px solid rgba(0,255,136,0.3)', background: 'var(--glass2)' }}>
      <div style={{ padding: '16px 18px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', letterSpacing: '0.1em' }}>⊹ VERÖFFENTLICHEN</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        {gbpTooLong && <div style={{ padding: '7px 11px', background: 'rgba(255,0,85,0.06)', border: '1px solid rgba(255,0,85,0.3)', borderRadius: 8, marginBottom: 12 }}><p style={{ fontSize: 11, color: 'var(--red)' }}>✕ Google Business Text zu lang ({gbpText.length}/1500 Zeichen).</p></div>}

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 8 }}>1 · PLATTFORMEN</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {publishablePlatforms.map(id => {
              const pl = ALL_PLATFORMS[id] || { label: id, color: '#888' };
              const active = selectedPlats.includes(id);
              return <button key={id} onClick={() => togglePlatform(id)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', background: active ? `${pl.color}18` : 'var(--glass)', color: active ? pl.color : 'var(--muted)', border: `1px solid ${active ? pl.color + '55' : 'var(--glass-border)'}`, fontWeight: active ? 700 : 400 }}>{active ? '✓ ' : ''}{pl.label}</button>;
            })}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 8 }}>2 · BILD (OPTIONAL)</div>
          {!showImgSection ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={handleGenerateImage} disabled={imageLoading} className="btn-ghost" style={{ fontSize: 12 }}>{imageLoading ? <><Spinner /> Generiert...</> : '✦ KI-Bild generieren'}</button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUploadImage} style={{ display: 'none' }} />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="btn-ghost" style={{ fontSize: 12 }}>{uploading ? <><Spinner /> Lädt...</> : '📁 Hochladen'}</button>
              <div style={{ display: 'flex', gap: 5 }}>{IMAGE_STYLES.map(s => <button key={s.id} onClick={() => setImageStyle(s.id)} style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10, cursor: 'pointer', background: imageStyle===s.id ? 'var(--cyan-dim)' : 'transparent', color: imageStyle===s.id ? 'var(--cyan)' : 'var(--muted)', border: `1px solid ${imageStyle===s.id ? 'rgba(0,255,255,0.25)' : 'var(--glass-border)'}` }}>{s.icon}</button>)}</div>
            </div>
          ) : (
            activeImage && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
                <img src={activeImage} alt="Bild" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--glass-border2)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 12, color: 'var(--green)', marginBottom: 6 }}>✓ Bild für alle Plattformen</p>
                  {textPlatforms.filter(p => selectedPlats.includes(p)).length > 0 && (
                    <div>
                      <p style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 5 }}>Ohne Bild posten:</p>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {textPlatforms.filter(p => selectedPlats.includes(p)).map(id => {
                          const pl = ALL_PLATFORMS[id] || { label: id, color: '#888' };
                          const off = noImagePlatforms.includes(id);
                          return <button key={id} onClick={() => toggleNoImage(id)} style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', background: off ? 'var(--glass2)' : `${pl.color}18`, color: off ? 'var(--muted)' : pl.color, border: `1px solid ${off ? 'var(--glass-border)' : pl.color + '40'}`, textDecoration: off ? 'line-through' : 'none' }}>{off ? '○' : '🖼'} {pl.label}</button>;
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={() => { setImageData(null); setSavedImage(null); setShowImgSection(false); }} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14, flexShrink: 0 }}>✕</button>
              </div>
            )
          )}
          {imageError && <p style={{ fontSize: 11, color: 'var(--red)', marginTop: 6 }}>{imageError}</p>}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 8 }}>3 · WANN</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[['now','⊹','Jetzt sofort','var(--green-dim)','rgba(0,255,136,0.5)','var(--green)'],['later','📅','Geplant','var(--cyan-dim)','rgba(0,229,255,0.5)','var(--cyan)']].map(([m,ic,t,bg,bc,c]) => (
              <button key={m} onClick={() => setMode(m)} style={{ padding: '10px 14px', borderRadius: 10, cursor: 'pointer', background: mode===m ? bg : 'var(--glass)', border: `2px solid ${mode===m ? bc : 'var(--glass-border)'}`, color: mode===m ? c : 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 16 }}>{ic}</span><span style={{ fontSize: 13, fontWeight: 600 }}>{t}</span></button>
            ))}
          </div>
          {mode === 'later' && selectedPlats.length > 0 && (
            <div className="fade-in" style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', padding: '10px 12px', background: 'var(--glass)', borderRadius: '9px 9px 0 0', border: '1px solid var(--glass-border2)', borderBottom: 'none' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>FÜR ALLE</div>
                  <input type="date" value={quickDate} onChange={e => setQuickDate(e.target.value)} min={minDate} style={{ width: '100%', padding: '7px 9px', background: 'var(--glass2)', border: '1px solid var(--glass-border2)', borderRadius: 7, color: 'var(--text)', fontSize: 12, colorScheme: 'auto', boxSizing: 'border-box' }} />
                </div>
                <div style={{ width: 110 }}>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>UHRZEIT</div>
                  <input type="time" value={quickTime} onChange={e => setQuickTime(e.target.value)} style={{ width: '100%', padding: '7px 9px', background: 'var(--glass2)', border: '1px solid var(--glass-border2)', borderRadius: 7, color: 'var(--text)', fontSize: 12, colorScheme: 'auto', boxSizing: 'border-box' }} />
                </div>
                <button onClick={() => { if (!quickDate) return; setScheduleMap(prev => { const next = { ...prev }; for (const pl of selectedPlats) next[pl] = { date: quickDate, time: quickTime }; return next; }); }} disabled={!quickDate} className="btn-ghost" style={{ fontSize: 11, padding: '7px 12px', flexShrink: 0, opacity: quickDate ? 1 : 0.4 }}>→ Alle</button>
              </div>
              <div style={{ padding: '8px 12px 4px', background: 'var(--glass)', border: '1px solid var(--glass-border2)', borderTop: '1px solid var(--glass-border)', borderRadius: '0 0 9px 9px' }}>
                {selectedPlats.map(id => {
                  const pl    = ALL_PLATFORMS[id] || { label: id, color: '#888' };
                  const entry = scheduleMap[id] || { date: '', time: '09:00' };
                  const best  = BEST_TIMES[id];
                  return (
                    <div key={id} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                        <div style={{ width: 88, fontSize: 11, color: pl.color, fontWeight: 600, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pl.label}</div>
                        <input type="date" value={entry.date} onChange={e => setScheduleMap(prev => ({ ...prev, [id]: { ...prev[id], date: e.target.value } }))} min={minDate} style={{ flex: 1, padding: '6px 8px', background: 'var(--glass2)', border: `1px solid ${entry.date ? pl.color + '50' : 'var(--glass-border2)'}`, borderRadius: 7, color: 'var(--text)', fontSize: 12, colorScheme: 'auto', boxSizing: 'border-box' }} />
                        <input type="time" value={entry.time || '09:00'} onChange={e => setScheduleMap(prev => ({ ...prev, [id]: { ...prev[id], time: e.target.value } }))} style={{ width: 90, padding: '6px 8px', background: 'var(--glass2)', border: `1px solid ${entry.date ? pl.color + '50' : 'var(--glass-border2)'}`, borderRadius: 7, color: 'var(--text)', fontSize: 12, colorScheme: 'auto', boxSizing: 'border-box' }} />
                        {best && <button onClick={() => setScheduleMap(prev => ({ ...prev, [id]: { ...prev[id], time: best.time } }))} title={best.hint} style={{ fontSize: 10, padding: '6px 8px', borderRadius: 7, cursor: 'pointer', background: `${pl.color}12`, border: `1px solid ${pl.color}30`, color: pl.color, flexShrink: 0, whiteSpace: 'nowrap' }}>⊹ {best.time}</button>}
                      </div>
                      {best && <div style={{ paddingLeft: 95, marginTop: 3 }}><span style={{ fontSize: 10, color: 'var(--muted)' }}>{best.hint}</span></div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: '0 18px 16px' }}>
        {error && <div style={{ padding: '8px 12px', background: 'var(--red-dim)', border: '1px solid rgba(255,0,85,0.3)', borderRadius: 8, marginBottom: 10 }}><p style={{ color: 'var(--red)', fontSize: 12 }}>{error}</p></div>}
        <button onClick={handlePublish} disabled={publishing || !canPublish} className="btn-primary" style={{ width: '100%', padding: '12px', fontSize: 13, fontWeight: 700, opacity: canPublish ? 1 : 0.4 }}>
          {publishing ? <><Spinner /> Wird veröffentlicht...</> : hasSchedule ? `📅 Planen — ${selectedPlats.length} Plattform${selectedPlats.length !== 1 ? 'en' : ''}` : `⊹ Jetzt veröffentlichen — ${selectedPlats.length} Plattform${selectedPlats.length !== 1 ? 'en' : ''}`}
        </button>
      </div>
    </div>
  );
}

function VariantRow({ variant, topic, token, onApprove, onSave, connectedPlatforms, onReload }) {
  const [editing,    setEditing]    = useState(false);
  const [content,    setContent]    = useState(variant.content);
  const [copied,     setCopied]     = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelMsg,  setCancelMsg]  = useState('');
  const p           = ALL_PLATFORMS[variant.platform] || { label: variant.platform, color: '#888', short: '?' };
  const isScheduled = !!variant.scheduled_for;
  React.useEffect(() => { if (!editing) setContent(variant.content); }, [variant.content, editing]);
  function handleCopy()   { navigator.clipboard.writeText(variant.content); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  function handleSave()   { onSave(content); setEditing(false); }
  function handleCancel() { setContent(variant.content); setEditing(false); }
  async function handleCancelSchedule() {
    if (!window.confirm('Geplante Veroeffentlichung stornieren?')) return;
    setCancelling(true);
    try { await api('/uploadpost/cancel/' + variant.id, { method: 'DELETE' }, token); setCancelMsg('Planung storniert'); setTimeout(() => setCancelMsg(''), 3000); if (onReload) onReload(); }
    catch (err) { setCancelMsg('Fehler: ' + err.message); }
    finally { setCancelling(false); }
  }
  return (
    <div style={{ borderRadius: 10, overflow: 'hidden', background: 'var(--glass2)', border: '1px solid ' + (isScheduled ? 'rgba(109,40,217,0.3)' : variant.approved ? 'rgba(0,255,136,0.2)' : 'var(--glass-border)') }}>
      {isScheduled && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 13px', background: 'rgba(109,40,217,0.08)', borderBottom: '1px solid rgba(109,40,217,0.2)', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 12 }}>📅</span><span style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>Geplant: {new Date(variant.scheduled_for).toLocaleString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })} Uhr</span></div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {cancelMsg && <span style={{ fontSize: 11, color: cancelMsg.startsWith('Planung') ? 'var(--green)' : 'var(--red)' }}>{cancelMsg}</span>}
            <button onClick={handleCancelSchedule} disabled={cancelling} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, cursor: 'pointer', background: 'rgba(255,0,85,0.08)', border: '1px solid rgba(255,0,85,0.25)', color: 'var(--red)' }}>{cancelling ? '...' : 'Stornieren'}</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '8px 13px', background: variant.approved ? 'rgba(0,255,136,0.04)' : 'var(--glass)', borderBottom: '1px solid var(--glass-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 22, height: 22, borderRadius: 5, fontSize: 9, fontWeight: 800, background: p.color + '20', color: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid ' + p.color + '30' }}>{p.short}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{p.label}</span>
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
          {!editing ? (<><button className="btn-ghost" onClick={() => setEditing(true)} style={{ fontSize: 11, padding: '3px 9px' }}>Bearbeiten</button><button className="btn-ghost" onClick={handleCopy} style={{ fontSize: 11, padding: '3px 9px', color: copied ? 'var(--green)' : undefined }}>{copied ? 'Kopiert' : 'Kopieren'}</button></>) : (<><button className="btn-ghost" onClick={handleSave} style={{ fontSize: 11, padding: '3px 9px', color: 'var(--cyan)' }}>Speichern</button><button className="btn-ghost" onClick={handleCancel} style={{ fontSize: 11, padding: '3px 9px' }}>Abbrechen</button></>)}
          <button onClick={onApprove} style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 7, cursor: 'pointer', background: variant.approved ? 'rgba(0,255,136,0.1)' : 'transparent', color: variant.approved ? 'var(--green)' : 'var(--text2)', border: '1px solid ' + (variant.approved ? 'rgba(0,255,136,0.3)' : 'var(--glass-border)') }}>{variant.approved ? 'Freigegeben' : 'Freigeben'}</button>
        </div>
      </div>
      <div style={{ padding: '10px 14px' }}>
        {editing ? (<><textarea value={content} onChange={e => setContent(e.target.value)} rows={Math.max(5, content.split('\n').length + 2)} className="input" style={{ lineHeight: 1.75, fontSize: 13, resize: 'vertical' }} autoFocus /><div style={{ display: 'flex', gap: 8, marginTop: 8 }}><button onClick={handleSave} className="btn-primary" style={{ fontSize: 12, padding: '6px 16px' }}>Speichern</button><button onClick={handleCancel} className="btn-ghost" style={{ fontSize: 12 }}>Abbrechen</button></div></>) : (<p style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{variant.content}</p>)}
      </div>
    </div>
  );
}

function Spinner() {
  return <span style={{ width: 11, height: 11, border: '2px solid var(--glass-border2)', borderTopColor: 'var(--cyan)', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block', flexShrink: 0 }} />;
}

function BlogTab({ token, drafts, loading, onReload }) {
  const [expanded,        setExpanded]        = useState(null);
  const [fullDraft,       setFullDraft]       = useState({});
  const [deleting,        setDeleting]        = useState(null);
  const [copied,          setCopied]          = useState(null);
  const [generatingImage, setGeneratingImage] = useState(null);
  const [publishing,      setPublishing]      = useState(null);
  const [imgError,        setImgError]        = useState({});
  const [pubError,        setPubError]        = useState({});
  const [imgProvider,     setImgProvider]     = useState('gemini');

  async function loadFull(id) {
    if (fullDraft[id]) { setExpanded(expanded === id ? null : id); return; }
    try { const d = await api(`/blog/${id}`, {}, token); setFullDraft(p => ({ ...p, [id]: d })); setExpanded(id); } catch {}
  }

  async function handleDelete(id) {
    if (!window.confirm('Blog-Entwurf löschen?')) return;
    setDeleting(id);
    try { await api(`/blog/${id}`, { method: 'DELETE' }, token); onReload(); } catch (err) { alert(err.message); } finally { setDeleting(null); }
  }

  function handleCopy(id) {
    const d = fullDraft[id]; if (!d) return;
    navigator.clipboard.writeText(d.content || ''); setCopied(id); setTimeout(() => setCopied(null), 2500);
  }

  async function handleGenerateImage(id) {
    setGeneratingImage(id);
    setImgError(p => ({ ...p, [id]: '' }));
    try {
      const data = await api(`/blog/${id}/image`, { method: 'POST', body: JSON.stringify({ provider: imgProvider }) }, token);
      setFullDraft(p => ({ ...p, [id]: { ...p[id], cover_image_data: data.image } }));
    } catch (err) {
      setImgError(p => ({ ...p, [id]: err.message }));
    } finally {
      setGeneratingImage(null);
    }
  }

  async function handleDeleteImage(id) {
    try {
      await api(`/blog/${id}/image`, { method: 'DELETE' }, token);
      setFullDraft(p => ({ ...p, [id]: { ...p[id], cover_image_data: null } }));
    } catch {}
  }

  async function handlePublish(id) {
    if (!window.confirm('Blog-Artikel im CMS als Entwurf anlegen?')) return;
    setPublishing(id);
    setPubError(p => ({ ...p, [id]: '' }));
    try {
      const data = await api(`/cms/publish/${id}`, { method: 'POST' }, token);
      setFullDraft(p => ({ ...p, [id]: { ...p[id], cms_published: true } }));
      alert(`Im CMS angelegt. Admin-URL:\n${data.result?.adminUrl || ''}`);
    } catch (err) {
      setPubError(p => ({ ...p, [id]: err.message }));
    } finally {
      setPublishing(null);
    }
  }

  if (loading) return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[1,2,3].map(i => <div key={i} className="shimmer" style={{ height: 60, borderRadius: 10 }} />)}</div>;
  if (drafts.length === 0) return (
    <div className="glass" style={{ padding: '36px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.3 }}>📝</div>
      <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 6 }}>Noch keine Blog-Entwürfe</p>
      <p style={{ fontSize: 12, color: 'var(--muted)' }}>Klicke auf "📝 Blog" beim Post generieren um deinen ersten Blogartikel zu erstellen.</p>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {drafts.map(draft => {
        const isOpen = expanded === draft.id;
        const full   = fullDraft[draft.id];
        const hasCover = full?.cover_image_data || draft.has_cover_image;
        const isPublished = full?.cms_published ?? draft.cms_published;
        return (
          <div key={draft.id} className="glass" style={{ overflow: 'hidden', border: isPublished ? '1px solid rgba(0,255,136,0.2)' : undefined }}>
            {/* Header-Zeile */}
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, cursor: 'pointer' }} onClick={() => loadFull(draft.id)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }} className="truncate">{draft.title || draft.topic}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{new Date(draft.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  {draft.word_count && <span style={{ fontSize: 10, color: 'var(--cyan)', padding: '1px 7px', background: 'var(--cyan-dim)', borderRadius: 10 }}>~{draft.word_count} Wörter</span>}
                  {draft.reading_time && <span style={{ fontSize: 10, color: 'var(--muted)' }}>⏱ {draft.reading_time}</span>}
                  {hasCover && <span style={{ fontSize: 10, color: 'var(--gold)', padding: '1px 7px', background: 'rgba(200,164,90,0.1)', borderRadius: 10, border: '1px solid rgba(200,164,90,0.2)' }}>🖼 Cover</span>}
                  {isPublished && <span style={{ fontSize: 10, color: 'var(--green)', padding: '1px 7px', background: 'rgba(0,255,136,0.08)', borderRadius: 10, border: '1px solid rgba(0,255,136,0.2)' }}>✓ CMS</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                {full && <button onClick={e => { e.stopPropagation(); handleCopy(draft.id); }} className="btn-ghost" style={{ fontSize: 11, padding: '3px 10px', color: copied===draft.id ? 'var(--green)' : undefined }}>{copied===draft.id ? '✓ Kopiert' : '⍘ Kopieren'}</button>}
                <button onClick={e => { e.stopPropagation(); handleDelete(draft.id); }} disabled={deleting===draft.id} className="btn-ghost" style={{ fontSize: 11, padding: '3px 10px', color: 'var(--red)' }}>{deleting===draft.id ? '...' : '✕'}</button>
                <span style={{ fontSize: 12, color: 'var(--muted)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>▾</span>
              </div>
            </div>

            {/* Ausgeklappter Bereich */}
            {isOpen && full && (
              <div className="fade-in" style={{ borderTop: '1px solid var(--glass-border)' }}>
                {/* SEO Meta */}
                {full.meta_description && (
                  <div style={{ padding: '8px 16px', background: 'rgba(200,164,90,0.06)', borderBottom: '1px solid rgba(200,164,90,0.15)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--gold)', flexShrink: 0, marginTop: 1 }}>SEO META</span>
                    <span style={{ fontSize: 11, color: 'var(--text2)' }}>{full.meta_description}</span>
                  </div>
                )}

                {/* Cover-Bild */}
                {full.cover_image_data && (
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', background: 'var(--glass2)' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gold)', letterSpacing: '0.08em', marginBottom: 8 }}>COVER-BILD (16:9)</div>
                    <img
                      src={full.cover_image_data}
                      alt="Cover"
                      style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--glass-border2)', display: 'block' }}
                    />
                    <div style={{ display: 'flex', gap: 7, marginTop: 8 }}>
                      <button onClick={() => { const a = document.createElement('a'); a.href = full.cover_image_data; a.download = 'cover.png'; a.click(); }} className="btn-ghost" style={{ fontSize: 10 }}>Herunterladen</button>
                      <button onClick={() => handleGenerateImage(draft.id)} disabled={!!generatingImage} className="btn-ghost" style={{ fontSize: 10 }}>{generatingImage===draft.id ? 'Generiert...' : '↺ Neu generieren'}</button>
                      <button onClick={() => handleDeleteImage(draft.id)} className="btn-ghost" style={{ fontSize: 10, color: 'var(--red)' }}>✕ Entfernen</button>
                    </div>
                  </div>
                )}

                {/* Artikel-Text */}
                <div style={{ padding: '16px', maxHeight: 400, overflowY: 'auto' }}>
                  <pre style={{ fontSize: 12, lineHeight: 1.85, color: 'var(--text2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', margin: 0 }}>{full.content}</pre>
                </div>

                {/* Aktions-Leiste */}
                <div style={{ padding: '10px 16px', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button onClick={() => handleCopy(draft.id)} className="btn-primary" style={{ fontSize: 12, padding: '7px 16px' }}>{copied===draft.id ? '✓ Kopiert!' : '⍘ Inhalt kopieren'}</button>

                  {/* Cover-Bild generieren — mit Provider-Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ display: 'flex', borderRadius: 7, overflow: 'hidden', border: '1px solid var(--glass-border)' }}>
                      {[['gemini','Gemini'],['flux','Flux Pro']].map(([id, label]) => (
                        <button key={id} onClick={() => setImgProvider(id)} style={{ padding: '4px 10px', fontSize: 10, cursor: 'pointer', background: imgProvider===id ? (id==='flux' ? 'rgba(200,164,90,0.15)' : 'var(--cyan-dim)') : 'transparent', color: imgProvider===id ? (id==='flux' ? 'var(--gold)' : 'var(--cyan)') : 'var(--text2)', border: 'none', fontWeight: imgProvider===id ? 600 : 400 }}>{label}</button>
                      ))}
                    </div>
                    <button
                      onClick={() => handleGenerateImage(draft.id)}
                      disabled={!!generatingImage}
                      className="btn-ghost"
                      style={{ fontSize: 12 }}
                    >
                      {generatingImage===draft.id ? <><Spinner /> Generiert...</> : full.cover_image_data ? '↺ Neu' : '🖼 Cover generieren'}
                    </button>
                  </div>

                  {!full.cms_published ? (
                    <button
                      onClick={() => handlePublish(draft.id)}
                      disabled={!!publishing}
                      className="btn-ghost"
                      style={{ fontSize: 12, color: 'var(--cyan)', borderColor: 'rgba(0,255,255,0.25)' }}
                    >
                      {publishing===draft.id ? <><Spinner /> Veröffentliche...</> : '⬡ Im CMS anlegen'}
                    </button>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="dot-live" /> Im CMS veröffentlicht
                    </span>
                  )}

                  <button onClick={() => handleDelete(draft.id)} className="btn-ghost" style={{ fontSize: 12, color: 'var(--red)', marginLeft: 'auto' }}>✕ Löschen</button>
                </div>

                {/* Fehlermeldungen */}
                {imgError[draft.id] && <div style={{ padding: '6px 16px 10px', fontSize: 11, color: 'var(--red)' }}>Bild: {imgError[draft.id]}</div>}
                {pubError[draft.id] && <div style={{ padding: '6px 16px 10px', fontSize: 11, color: 'var(--red)' }}>CMS: {pubError[draft.id]}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
